import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ActionButtons, DateCell } from './sectionShared';
import {
  DescriptionCell,
  ImpactCell,
  IssueIdentityCell,
  RiskIdentityCell,
} from './riskRegisterTableCells';
import { extractTrailingNumber, sortRows, toComparableText } from './riskRegisterSortUtils';

const infoSecRiskScoreTooltip = (
  <div className="risk-score-tooltip">
    <div>Risk Priority Number (RPN)=Total Asset Value * Likelihood * Impact</div>
    <div>Risk Score=High if RPN &gt; 296, Medium if 108 &lt; RPN &lt; 296, Low if RPN &lt; 108</div>
  </div>
);
const isNumericLike = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  if (!text) return false;
  return Number.isFinite(Number(text));
};

const getNumericCellClass = (value) => (isNumericLike(value) ? 'td-numeric' : '');

const isNumericOnlyColumn = (rows, accessor) => {
  const values = (Array.isArray(rows) ? rows : [])
    .map((row) => accessor(row))
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== '');

  return values.length > 0 && values.every((value) => isNumericLike(value));
};

const getSortIconClass = (activeKey, activeDir, key) => (
  activeKey === key ? `is-sorted is-${activeDir}` : ''
);

const handleHeaderKeyDown = (event, onSort) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onSort();
  }
};

const SortableTh = ({
  className,
  label,
  sortKey,
  activeKey,
  activeDir,
  onSort,
}) => (
  <th
    className={className}
    onClick={onSort}
    onKeyDown={(event) => handleHeaderKeyDown(event, onSort)}
    role="button"
    tabIndex={0}
  >
    <div className="th-content">
      <span className="th-label">{label}</span>
      <span className={`sort-icon ${getSortIconClass(activeKey, activeDir, sortKey)}`} />
    </div>
  </th>
);

SortableTh.propTypes = {
  className: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  sortKey: PropTypes.string.isRequired,
  activeKey: PropTypes.string.isRequired,
  activeDir: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
};

const useSortState = (defaultKey = 'id', defaultDir = 'desc') => {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = (nextKey) => {
    if (sortKey === nextKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(nextKey);
    setSortDir('asc');
  };

  return {
    sortKey,
    sortDir,
    handleSort,
  };
};

const IssueTable = ({
  issueRows,
  canEditIssue,
  canDeleteIssue,
  handleIssueEdit,
  handleIssueDelete,
}) => {
  const { sortKey, sortDir, handleSort } = useSortState('id', 'desc');
  const showActions = canEditIssue || canDeleteIssue;

  const issueNumericClasses = [
    isNumericOnlyColumn(issueRows, (row) => row?.category) ? 'compact-numeric-col-4' : '',
    isNumericOnlyColumn(issueRows, (row) => row?.priority) ? 'compact-numeric-col-5' : '',
  ].filter(Boolean).join(' ');

  const sortedIssueRows = useMemo(() => sortRows(
    issueRows,
    sortKey,
    sortDir,
    (row, key) => {
      switch (key) {
      case 'id':
        return extractTrailingNumber(row?.id) ?? extractTrailingNumber(row?.row_id) ?? null;
      case 'description':
        return toComparableText(row?.description);
      case 'category':
        return toComparableText(row?.category);
      case 'priority':
        return Number.isFinite(Number(row?.priority))
          ? Number(row?.priority)
          : toComparableText(row?.priority);
      case 'assignedTo':
        return toComparableText(row?.assignedTo);
      case 'raised_on_date':
        return toComparableText(row?.raised_on_date);
      case 'target_date':
        return toComparableText(row?.target_date);
      case 'revised_target_date':
        return toComparableText(row?.revised_target_date);
      case 'statusUpdates':
        return toComparableText(row?.statusUpdates);
      case 'last_updated_date':
        return toComparableText(row?.last_updated_date);
      default:
        return toComparableText(row?.[key]);
      }
    },
  ), [issueRows, sortKey, sortDir]);

  return (
    <div className="risk-table-wrapper">
      <div className="risk-table-scroll">
        <table className={`issue-data-table ${issueNumericClasses}`.trim()}>
          <thead>
            <tr>
              <th className="th-cell th-serial">
                <div className="th-content"><span>Si. No</span></div>
              </th>
              <SortableTh
                className="th-cell sortable"
                label="Issues"
                sortKey="id"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('id')}
              />
              <SortableTh
                className="th-cell sortable th-description"
                label="Issue description"
                sortKey="description"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('description')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Issue category"
                sortKey="category"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('category')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Priority"
                sortKey="priority"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('priority')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Assigned to"
                sortKey="assignedTo"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('assignedTo')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Raised on"
                sortKey="raised_on_date"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('raised_on_date')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Target date"
                sortKey="target_date"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('target_date')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Revised Target Date"
                sortKey="revised_target_date"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('revised_target_date')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Status Updates/Actions taken"
                sortKey="statusUpdates"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('statusUpdates')}
              />
              <SortableTh
                className="th-cell sortable"
                label="Last Updated Date"
                sortKey="last_updated_date"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={() => handleSort('last_updated_date')}
              />
              {showActions ? (
                <th className="th-cell th-actions">
                  <div className="th-content th-actions-content"><span>Actions</span></div>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sortedIssueRows.map((row, idx) => {
              const rowKey = row.id || idx;

              return (
                <tr key={rowKey} className="tr-data">
                  <td className="td-cell td-serial">{idx + 1}</td>
                  <IssueIdentityCell row={row} />
                  <DescriptionCell value={row.description} />
                  <td className={`td-cell ${getNumericCellClass(row.category)}`.trim()}>{row.category || ''}</td>
                  <td className={`td-cell ${getNumericCellClass(row.priority)}`.trim()}>{row.priority || ''}</td>
                  <td className="td-cell">{row.assignedTo || ''}</td>
                  <td className="td-cell">
                    <DateCell date={row.raisedDate} day={row.raisedDay} />
                  </td>
                  <td className="td-cell">{row.targetDate || ''}</td>
                  <td className="td-cell">{row.revisedDate || ''}</td>
                  <td className="td-cell">{row.statusUpdates || ''}</td>
                  <td className="td-cell">
                    <DateCell date={row.lastUpdated} day={row.lastUpdatedDay} />
                  </td>
                  {showActions ? (
                    <td className="td-cell td-actions">
                      <ActionButtons
                        onEdit={canEditIssue ? () => handleIssueEdit(row) : null}
                        onDelete={canDeleteIssue ? () => handleIssueDelete(row) : null}
                      />
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RiskTableHeader = ({
  isInfoSecTab,
  sortKey,
  sortDir,
  handleSort,
  showActions,
}) => (
  <thead>
    <tr>
      <th className="th-cell th-serial">
        <div className="th-content"><span>Si. No</span></div>
      </th>
      <SortableTh
        className="th-cell sortable"
        label={isInfoSecTab ? 'InfoSec Issues' : 'Risk'}
        sortKey="id"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('id')}
      />
      <SortableTh
        className="th-cell sortable th-description"
        label={isInfoSecTab ? 'InfoSec description' : 'Risk description'}
        sortKey="description"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('description')}
      />
      <SortableTh
        className="th-cell sortable"
        label={isInfoSecTab ? 'Asset category' : 'Risk category'}
        sortKey="category"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('category')}
      />
      <SortableTh
        className="th-cell sortable"
        label={isInfoSecTab ? (
          <span className="th-label-with-tooltip">
            Risk score
            <Tooltip title={infoSecRiskScoreTooltip} arrow placement="top">
              <span
                className="info-tooltip-icon"
                role="button"
                tabIndex={0}
                aria-label="Risk score info"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </span>
            </Tooltip>
          </span>
        ) : 'Risk score'}
        sortKey="riskScore"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('riskScore')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Likelihood"
        sortKey="likelihood"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('likelihood')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Impact"
        sortKey="impact"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('impact')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Owner"
        sortKey="owner"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('owner')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Raised on"
        sortKey="raised_on_date"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('raised_on_date')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Target date"
        sortKey="target_date"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('target_date')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Revised Target Date"
        sortKey="revised_target_date"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('revised_target_date')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Risk mitigation strategy"
        sortKey="mitigation"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('mitigation')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Measures to address risk"
        sortKey="measures"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('measures')}
      />
      <SortableTh
        className="th-cell sortable"
        label="Last Updated Date"
        sortKey="last_updated_date"
        activeKey={sortKey}
        activeDir={sortDir}
        onSort={() => handleSort('last_updated_date')}
      />
      {showActions ? (
        <th className="th-cell th-actions">
          <div className="th-content th-actions-content"><span>Actions</span></div>
        </th>
      ) : null}
    </tr>
  </thead>
);

RiskTableHeader.propTypes = {
  isInfoSecTab: PropTypes.bool.isRequired,
  sortKey: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  handleSort: PropTypes.func.isRequired,
  showActions: PropTypes.bool.isRequired,
};

const RiskTableRow = ({
  row,
  index,
  showActions,
  canEdit,
  canDelete,
  isInfoSecTab,
  handleInfoSecEdit,
  handleRiskEdit,
  handleInfoSecDelete,
  handleRiskDelete,
}) => {
  const riskScoreLabel = String(row?.riskScore || '').trim();
  const riskScoreDisplay = riskScoreLabel ? riskScoreLabel.toUpperCase() : '';
  const riskScoreTone = riskScoreLabel.toLowerCase();

  return (
    <tr key={row.id || index} className="tr-data">
      <td className="td-cell td-serial">{index + 1}</td>
      <RiskIdentityCell row={row} />
      <DescriptionCell value={row.description} />
      <td className={`td-cell ${getNumericCellClass(row.category || row.assetCategory)}`.trim()}>
        {row.category || row.assetCategory || ''}
      </td>
      <td className={`td-cell ${getNumericCellClass(row.riskScore)}`.trim()}>
        {riskScoreLabel ? (
          <span className={`risk-score-badge ${riskScoreTone}`.trim()}>
            {riskScoreDisplay}
          </span>
        ) : ''}
      </td>
      <td className={`td-cell ${getNumericCellClass(row.likelihood)}`.trim()}>{row.likelihood || ''}</td>
      <ImpactCell value={row.impact} className={getNumericCellClass(row.impact)} />
      <td className="td-cell">{row.owner || ''}</td>
      <td className="td-cell">
        <DateCell date={row.raisedDate} day={row.raisedDay} />
      </td>
      <td className="td-cell">{row.targetDate || ''}</td>
      <td className="td-cell">{row.revisedDate || ''}</td>
      <td className={`td-cell ${getNumericCellClass(row.mitigation)}`.trim()}>{row.mitigation || ''}</td>
      <td className="td-cell">{row.measures || ''}</td>
      <td className="td-cell">
        <DateCell date={row.lastUpdated} day={row.lastUpdatedDay} />
      </td>
      {showActions ? (
        <td className="td-cell td-actions">
          <ActionButtons
            onEdit={canEdit ? () => (isInfoSecTab ? handleInfoSecEdit(row) : handleRiskEdit(row)) : null}
            onDelete={canDelete ? () => (
              isInfoSecTab ? handleInfoSecDelete(row) : handleRiskDelete(row)
            ) : null}
          />
        </td>
      ) : null}
    </tr>
  );
};

RiskTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  showActions: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  isInfoSecTab: PropTypes.bool.isRequired,
  handleInfoSecEdit: PropTypes.func.isRequired,
  handleRiskEdit: PropTypes.func.isRequired,
  handleInfoSecDelete: PropTypes.func.isRequired,
  handleRiskDelete: PropTypes.func.isRequired,
};

const RiskTable = ({
  isInfoSecTab,
  visibleRiskRows,
  canEditInfoSec,
  canEditRisk,
  canDeleteInfoSec,
  canDeleteRisk,
  handleInfoSecEdit,
  handleRiskEdit,
  handleInfoSecDelete,
  handleRiskDelete,
}) => {
  const { sortKey, sortDir, handleSort } = useSortState('id', 'desc');
  const canEdit = isInfoSecTab ? canEditInfoSec : canEditRisk;
  const canDelete = isInfoSecTab ? canDeleteInfoSec : canDeleteRisk;
  const showActions = canEdit || canDelete;

  const riskNumericClasses = [
    isNumericOnlyColumn(visibleRiskRows, (row) => row?.category || row?.assetCategory) ? 'compact-numeric-col-4' : '',
    isNumericOnlyColumn(visibleRiskRows, (row) => row?.riskScore) ? 'compact-numeric-col-5' : '',
    isNumericOnlyColumn(visibleRiskRows, (row) => row?.likelihood) ? 'compact-numeric-col-6' : '',
    isNumericOnlyColumn(visibleRiskRows, (row) => row?.impact) ? 'compact-numeric-col-7' : '',
    isNumericOnlyColumn(visibleRiskRows, (row) => row?.mitigation) ? 'compact-numeric-col-11' : '',
  ].filter(Boolean).join(' ');

  const sortedRiskRows = useMemo(() => sortRows(
    visibleRiskRows,
    sortKey,
    sortDir,
    (row, key) => {
      switch (key) {
      case 'id':
        return extractTrailingNumber(row?.id) ?? extractTrailingNumber(row?.row_id) ?? null;
      case 'description':
        return toComparableText(row?.description);
      case 'category':
        return toComparableText(row?.category || row?.assetCategory);
      case 'riskScore':
        return Number.isFinite(Number(row?.riskScoreValue))
          ? Number(row.riskScoreValue)
          : toComparableText(row?.riskScore);
      case 'likelihood':
        return Number.isFinite(Number(row?.likelihood_value))
          ? Number(row.likelihood_value)
          : toComparableText(row?.likelihood);
      case 'impact':
        return Number.isFinite(Number(row?.impact_value))
          ? Number(row.impact_value)
          : toComparableText(row?.impact);
      case 'owner':
        return toComparableText(row?.owner);
      case 'raised_on_date':
        return toComparableText(row?.raised_on_date);
      case 'target_date':
        return toComparableText(row?.target_date);
      case 'revised_target_date':
        return toComparableText(row?.revised_target_date);
      case 'mitigation':
        return toComparableText(row?.mitigation);
      case 'measures':
        return toComparableText(row?.measures);
      case 'last_updated_date':
        return toComparableText(row?.last_updated_date);
      default:
        return toComparableText(row?.[key]);
      }
    },
  ), [visibleRiskRows, sortKey, sortDir]);

  return (
    <div className="risk-table-wrapper">
      <div className="risk-table-scroll">
        <table className={`risk-data-table ${riskNumericClasses}`.trim()}>
          <RiskTableHeader
            isInfoSecTab={isInfoSecTab}
            sortKey={sortKey}
            sortDir={sortDir}
            handleSort={handleSort}
            showActions={showActions}
          />
          <tbody>
            {sortedRiskRows.map((row, idx) => (
              <RiskTableRow
                key={row.id || idx}
                row={row}
                index={idx}
                showActions={showActions}
                canEdit={canEdit}
                canDelete={canDelete}
                isInfoSecTab={isInfoSecTab}
                handleInfoSecEdit={handleInfoSecEdit}
                handleRiskEdit={handleRiskEdit}
                handleInfoSecDelete={handleInfoSecDelete}
                handleRiskDelete={handleRiskDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

IssueTable.propTypes = {
  issueRows: PropTypes.array.isRequired,
  canEditIssue: PropTypes.bool.isRequired,
  canDeleteIssue: PropTypes.bool.isRequired,
  handleIssueEdit: PropTypes.func.isRequired,
  handleIssueDelete: PropTypes.func.isRequired,
};
RiskTable.propTypes = {
  isInfoSecTab: PropTypes.bool.isRequired,
  visibleRiskRows: PropTypes.array.isRequired,
  canEditInfoSec: PropTypes.bool.isRequired,
  canEditRisk: PropTypes.bool.isRequired,
  canDeleteInfoSec: PropTypes.bool.isRequired,
  canDeleteRisk: PropTypes.bool.isRequired,
  handleInfoSecEdit: PropTypes.func.isRequired,
  handleRiskEdit: PropTypes.func.isRequired,
  handleInfoSecDelete: PropTypes.func.isRequired,
  handleRiskDelete: PropTypes.func.isRequired,
};

export { IssueTable, RiskTable };
